import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Sparkles, Check, Compass, Target, Loader2, ArrowRight } from "lucide-react";
import { shojinApi } from "../../lib/shojinApi";
import { trackEvent } from "../../lib/analytics";

/**
 * Shōjin Dō — the expanded 10-level curriculum + honest self-assessment.
 *
 * Lives inside the Wellbeing tab (below the hero, above the check-in) so it's
 * discoverable but doesn't hijack the daily surface. Anyone who read the
 * philosophy in the hero can now actually READ the levels + take the
 * assessment to find where they should start.
 */
export default function ShojinPractice() {
  const [levels, setLevels] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [openLevel, setOpenLevel] = useState(0);
  const [currentLevel, setCurrentLevel] = useState(null);
  const [assessOpen, setAssessOpen] = useState(false);
  const [answers, setAnswers] = useState({});
  const [assessing, setAssessing] = useState(false);
  const [assessResult, setAssessResult] = useState(null);

  useEffect(() => {
    Promise.all([
      shojinApi.curriculum().catch(() => ({ levels: [] })),
      shojinApi.assessment().catch(() => ({ questions: [] })),
      shojinApi.dailyCue().catch(() => null),
    ]).then(([c, a, cue]) => {
      setLevels(c?.levels || []);
      setQuestions(a?.questions || []);
      if (cue && typeof cue.level_idx === "number") setCurrentLevel(cue.level_idx);
    });
  }, []);

  const total = answers && questions.length
    ? questions.reduce((sum, q) => sum + (answers[q.id] ?? 0), 0)
    : 0;
  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id] !== undefined);

  const submitAssessment = async () => {
    if (!allAnswered) return;
    setAssessing(true);
    try {
      const r = await shojinApi.assess(total);
      setAssessResult(r);
      setCurrentLevel(r.level_idx);
      trackEvent("shojin_assessment_complete", { score: total, level_idx: r.level_idx });
    } catch { /* silent */ }
    finally { setAssessing(false); }
  };

  if (levels.length === 0) return null;

  return (
    <div data-testid="shojin-practice" className="rounded-2xl border border-white/5 bg-zinc-950/40 p-4 sm:p-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Compass className="w-3.5 h-3.5 text-[#BEFF38]" strokeWidth={2.5} />
            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-[#BEFF38]">
              The 10-level path
            </p>
          </div>
          <p className="mt-1.5 text-white text-sm font-bold leading-snug">
            Show Up First → Lifetime Athlete
          </p>
          <p className="mt-1 text-zinc-400 text-xs leading-relaxed">
            Each level is a specific practice you drill for 2-4 weeks before promotion. Read the whole ladder here. Take the honest 5-question assessment to find your starting level.
          </p>
        </div>
        <button
          onClick={() => { setAssessOpen((v) => !v); trackEvent("shojin_assessment_open"); }}
          data-testid="shojin-assess-toggle"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#BEFF38] text-black text-[11px] font-black uppercase tracking-wider hover:bg-lime-300"
        >
          <Sparkles className="w-3.5 h-3.5" /> {assessResult ? "Retake" : "Take assessment"}
        </button>
      </div>

      {/* Assessment flow */}
      {assessOpen && (
        <div data-testid="shojin-assess-panel" className="mt-4 rounded-xl border border-[#BEFF38]/20 bg-[#BEFF38]/5 p-4 space-y-3">
          <p className="text-[11px] uppercase tracking-wider font-black text-[#BEFF38]">Answer honestly. Behaviour, not aspiration.</p>
          {questions.map((q, i) => (
            <div key={q.id} data-testid={`shojin-q-${q.id}`}>
              <p className="text-white text-sm font-bold leading-snug">{i + 1}. {q.prompt}</p>
              <p className="mt-1 text-[11px] text-zinc-500 italic leading-snug">{q.form}</p>
              <div className="mt-2 inline-flex rounded-lg bg-white/5 p-0.5 border border-white/5">
                {[
                  { v: 2, label: "Yes" },
                  { v: 1, label: "Almost" },
                  { v: 0, label: "Not yet" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt.v }))}
                    data-testid={`shojin-q-${q.id}-${opt.label.toLowerCase().replace(/ /g, "-")}`}
                    className={`px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-wider transition-colors ${
                      answers[q.id] === opt.v ? "bg-[#BEFF38] text-black" : "text-zinc-400 hover:text-white"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={submitAssessment}
              disabled={!allAnswered || assessing}
              data-testid="shojin-assess-submit"
              className="inline-flex items-center gap-2 bg-[#BEFF38] text-black font-black uppercase tracking-wider text-xs px-4 py-2.5 rounded-lg hover:brightness-95 disabled:opacity-40"
            >
              {assessing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              See my starting level
            </button>
            <p className="text-[10px] text-zinc-500 tabular-nums">
              Score {total} / {questions.length * 2}
            </p>
          </div>
          {assessResult && (
            <div className="mt-3 rounded-lg border border-[#BEFF38]/40 bg-black/40 p-3" data-testid="shojin-assess-result">
              <div className="flex items-center gap-2">
                <Target className="w-3.5 h-3.5 text-[#BEFF38]" strokeWidth={2.5} />
                <p className="text-[10px] uppercase tracking-wider font-black text-[#BEFF38]">Your starting level</p>
              </div>
              <p className="mt-1.5 text-white text-sm font-bold">{assessResult.level.name}</p>
              <p className="mt-1 text-zinc-400 text-xs leading-relaxed">{assessResult.level.strategy}</p>
              <button
                onClick={() => { setOpenLevel(assessResult.level_idx); setAssessOpen(false); }}
                data-testid="shojin-goto-level"
                className="mt-3 inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider text-[#BEFF38] hover:underline"
              >
                Open Level {assessResult.level_idx + 1} <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Ladder */}
      <div className="mt-4 space-y-1.5">
        {levels.map((lvl, idx) => {
          const isOpen = openLevel === idx;
          const isCurrent = currentLevel === idx;
          return (
            <div
              key={lvl.id}
              data-testid={`shojin-level-${idx}`}
              className={`rounded-xl border transition-colors ${
                isCurrent ? "border-[#BEFF38]/40 bg-[#BEFF38]/5" : "border-zinc-800 bg-[#0b0c10]"
              }`}
            >
              <button
                onClick={() => setOpenLevel(isOpen ? -1 : idx)}
                className="w-full text-left p-3 flex items-start gap-3"
              >
                <span className={`shrink-0 w-7 h-7 rounded-full grid place-items-center text-[10px] font-black tabular-nums ${
                  isCurrent ? "bg-[#BEFF38] text-black" : "bg-white/5 text-zinc-500"
                }`}>
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <p className="text-white font-bold text-sm leading-tight">{lvl.name.replace(/^Level \d+ · /, "")}</p>
                    {isCurrent && <span className="text-[9px] font-black uppercase tracking-wider text-[#BEFF38]">You are here</span>}
                  </div>
                  <p className="mt-0.5 text-[11px] text-zinc-500 leading-snug">{lvl.format}</p>
                </div>
                {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0 mt-0.5" /> : <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0 mt-0.5" />}
              </button>
              {isOpen && (
                <div className="px-3 pb-3 space-y-3">
                  <Field label="The practice">{lvl.block}</Field>
                  <Field label="Target">{lvl.target}</Field>
                  <Field label="Why this level">{lvl.strategy}</Field>
                  {(lvl.form_cues || []).length > 0 && (
                    <Field label="Practice cues"><Bullets items={lvl.form_cues} /></Field>
                  )}
                  {(lvl.faults || []).length > 0 && (
                    <div className="rounded-md border border-red-500/25 bg-red-500/5 p-2.5">
                      <p className="text-[10px] uppercase tracking-wider font-black text-red-300">Common failure modes</p>
                      <Bullets items={lvl.faults} />
                    </div>
                  )}
                  <Field label="Scaling">{lvl.scaling}</Field>
                  <Field label="Unlock — when to promote to the next level">{lvl.unlock}</Field>
                  <p className="text-[10px] text-zinc-500 italic">
                    Minimum {lvl.weeks_min === 999 ? "∞ (this is the level you stay on)" : `${lvl.weeks_min} weeks`} before advancing.
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">{label}</p>
    <p className="mt-0.5 text-zinc-300 text-xs leading-relaxed">{children}</p>
  </div>
);

const Bullets = ({ items }) => (
  <ul className="mt-0.5 space-y-0.5 list-disc list-inside text-xs text-zinc-300 leading-relaxed">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);
